import { ExpandMoreIcon } from '@/assets/icons'
import { DropdownOption } from '@components/Dropdown'
import { usePortalDropdownPosition } from '@hooks/usePortalDropdownPosition'
import { iconSizes } from '@/theme/tokens'
import React, { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DayOption } from '../DayPane/MoveEventModal'
import { ChipChevron, ChipText, DayChip, DayMenu } from './DayMap.styles'

export interface DayPickerProps {
	/** What the chip says: the day's counter-and-destination phrase. */
	label: string
	/** The selected day's destination colour, matching its calendar cell. */
	colorIndex: number
	selectedDay: number
	days: DayOption[]
	onSelectDay: (dayIndex: number) => void
}

/**
 * The map's day label as a chip, and the day picker behind it: click the chip
 * for a portaled list of every trip day, pick one to select it — the
 * calendar's job, within reach of the map. The keyboard follows the Dropdown:
 * arrows move the highlight, Enter or Space picks, Escape closes.
 */
const DayPicker: React.FC<DayPickerProps> = ({ label, colorIndex, selectedDay, days, onSelectDay }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [highlighted, setHighlighted] = useState(0)
	const chipRef = useRef<HTMLButtonElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const position = usePortalDropdownPosition(chipRef, isOpen)
	const listboxId = useId()

	const selectedIndex = Math.max(
		0,
		days.findIndex(d => d.dayIndex === selectedDay),
	)
	const last = days.length - 1

	const open = () => {
		setHighlighted(selectedIndex)
		setIsOpen(true)
	}
	const close = () => setIsOpen(false)
	const pick = (day: DayOption) => {
		close()
		if (day.dayIndex !== selectedDay) onSelectDay(day.dayIndex)
	}

	useEffect(() => {
		if (!isOpen) return
		const onMouseDown = (event: MouseEvent) => {
			const target = event.target as Node
			if (!chipRef.current?.contains(target) && !menuRef.current?.contains(target)) close()
		}
		document.addEventListener('mousedown', onMouseDown)
		return () => document.removeEventListener('mousedown', onMouseDown)
	}, [isOpen])

	// A long trip's list scrolls: the highlighted day stays in view as it opens and as the arrows move it.
	useEffect(() => {
		if (!isOpen || !position) return
		menuRef.current?.querySelector<HTMLElement>(`[data-highlighted="true"]`)?.scrollIntoView?.({ block: 'nearest' })
	}, [isOpen, position, highlighted])

	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				if (!isOpen) open()
				else setHighlighted(index => Math.min(index + 1, last))
				break
			case 'ArrowUp':
				event.preventDefault()
				if (!isOpen) open()
				else setHighlighted(index => Math.max(index - 1, 0))
				break
			case 'Home':
				if (!isOpen) return
				event.preventDefault()
				setHighlighted(0)
				break
			case 'End':
				if (!isOpen) return
				event.preventDefault()
				setHighlighted(last)
				break
			case 'Enter':
			case ' ':
				event.preventDefault()
				if (!isOpen) open()
				else if (days[highlighted]) pick(days[highlighted])
				break
			case 'Escape':
				if (!isOpen) return
				event.preventDefault()
				close()
				break
			case 'Tab':
				close()
				break
		}
	}

	const optionId = (index: number) => `${listboxId}-${index}`

	return (
		<>
			<DayChip
				ref={chipRef}
				type="button"
				title="Change day"
				$colorIndex={colorIndex}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={isOpen ? listboxId : undefined}
				aria-activedescendant={isOpen ? optionId(highlighted) : undefined}
				onClick={() => (isOpen ? close() : open())}
				onKeyDown={handleKeyDown}
			>
				<ChipText>{label}</ChipText>
				<ChipChevron aria-hidden $open={isOpen}>
					<ExpandMoreIcon sx={{ fontSize: iconSizes.sm }} />
				</ChipChevron>
			</DayChip>
			{isOpen &&
				position &&
				createPortal(
					<DayMenu
						ref={menuRef}
						id={listboxId}
						role="listbox"
						aria-label="Trip days"
						$top={position.top}
						$left={position.left}
						$width={position.width}
					>
						{days.map((day, index) => (
							<DropdownOption
								key={day.dayIndex}
								id={optionId(index)}
								role="option"
								aria-selected={day.dayIndex === selectedDay}
								data-highlighted={index === highlighted}
								$selected={day.dayIndex === selectedDay}
								$highlighted={index === highlighted}
								onMouseEnter={() => setHighlighted(index)}
								onMouseDown={event => {
									event.preventDefault() // keep focus on the chip
									pick(day)
								}}
							>
								{day.label}
							</DropdownOption>
						))}
					</DayMenu>,
					document.body,
				)}
		</>
	)
}

export default DayPicker
